import { Injectable } from '@angular/core';
import { IEvento } from 'modelosSeguridad/src';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';

/**
 * EventosSeguridadLayerManager
 *
 * Renderiza eventos de seguridad (sub-categoría de alertas — `tipo='Evento'`)
 * en el mapa. Diferencia visual respecto a alertas: color violeta + icono propio.
 */
@Injectable({
  providedIn: 'root',
})
export class EventosSeguridadLayerManager extends BaseEntityLayerManager<IEvento> {
  readonly entityType = 'eventosSeguridad';
  readonly supportsClustering = true;
  readonly clusterColor = '#7B1FA2';

  private mainLayerId = 'eventosSeguridad-layer';
  private circleLayerId = 'eventosSeguridad-circle-layer';
  private unclusteredLayerId = 'eventosSeguridad-unclustered-layer';
  private unclusteredCircleLayerId = 'eventosSeguridad-unclustered-circle-layer';

  private readonly markerColor = '#7B1FA2';

  override entityToFeature(evento: IEvento): GeoJSON.Feature<GeoJSON.Point> {
    this.storeEntity(evento);

    let coordinates: [number, number];

    if (
      evento.geojson?.coordinates &&
      Array.isArray(evento.geojson.coordinates) &&
      evento.geojson.coordinates.length === 2
    ) {
      coordinates = evento.geojson.coordinates as [number, number];
    } else if (
      evento.coordenadas &&
      typeof evento.coordenadas.lng === 'number' &&
      typeof evento.coordenadas.lat === 'number'
    ) {
      coordinates = [evento.coordenadas.lng, evento.coordenadas.lat];
    } else {
      console.warn(`Evento seguridad ${evento._id} sin coordenadas válidas`);
      coordinates = [-58.443832, -34.587386];
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: evento._id,
        categoria: evento.categoria,
        fecha: evento.fecha,
        idCliente: evento.idCliente,
      },
    };
  }

  override getIconExpression(): string {
    return 'alerta-seguridad-icon';
  }

  protected override createLayers(): void {
    if (!this.map) return;

    if (this.supportsClustering) {
      this.createStandardClusterLayers();

      this.addLayer({
        id: this.unclusteredCircleLayerId,
        type: 'circle',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': 8,
          'circle-color': this.markerColor,
          'circle-opacity': 0.9,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
        layout: { visibility: 'none' },
      });

      this.addLayer({
        id: this.unclusteredLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-size': 0.025,
          'icon-allow-overlap': true,
          'icon-padding': 0,
          visibility: 'none',
        },
      });
    }

    this.addLayer({
      id: this.circleLayerId,
      type: 'circle',
      source: this.sourceId,
      paint: {
        'circle-radius': 8,
        'circle-color': this.markerColor,
        'circle-opacity': 0.9,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });

    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 0.025,
        'icon-allow-overlap': true,
        'icon-padding': 0,
      },
    });
  }

  protected override updateClusteringVisibility(): void {
    if (!this.map || !this.supportsClustering) return;

    if (this.clusteringEnabled) {
      this.setLayerVisibility(
        `${this.entityType}-cluster-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(this.unclusteredCircleLayerId, this.isVisible);
      this.setLayerVisibility(this.unclusteredLayerId, this.isVisible);
      this.setLayerVisibility(this.circleLayerId, false);
      this.setLayerVisibility(this.mainLayerId, false);
    } else {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
      this.setLayerVisibility(`${this.entityType}-cluster-count-layer`, false);
      this.setLayerVisibility(this.unclusteredCircleLayerId, false);
      this.setLayerVisibility(this.unclusteredLayerId, false);
      this.setLayerVisibility(this.circleLayerId, this.isVisible);
      this.setLayerVisibility(this.mainLayerId, this.isVisible);
    }
  }

  private setLayerVisibility(layerId: string, visible: boolean): void {
    if (!this.map) return;
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    if (this.clusteringEnabled && this.supportsClustering) {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, visible);
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        visible,
      );
      this.setLayerVisibility(this.unclusteredCircleLayerId, visible);
      this.setLayerVisibility(this.unclusteredLayerId, visible);
    } else {
      this.setLayerVisibility(this.circleLayerId, visible);
      this.setLayerVisibility(this.mainLayerId, visible);
    }
  }
}
